import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, "Another solution is subdomains. Each email sender uses their own subdomain and has their own DMARC policy. This eliminates the problem of merging incompatible DMARC policies, but there are some drawbacks to this approach.", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/using-different-dmarc-records-for-subdomains/multiple-dmarc-records.jpg",
    alt: "Some things are not designed to be used by two people",
    title: "Some things are not designed to be used by two people",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Some things are not designed to be used by two people. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", null, "Here's an example showing the DMARC records for a business' root domain and separate DMARC policy for promotional and support subdomains:", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("pre", null, "_dmarc.example.org.             3600  TXT  \"v=DMARC1; p=reject; aspf=s; rua=mailto:rua@example.org\"\n_dmarc.promotions.example.org.  3600  TXT  \"v=DMARC1; p=reject; rua=mailto:promo_rua@example.org\"\n_dmarc.support.example.org.     3600  TXT  \"v=DMARC1; p=reject; ri=604800; rua=mailto:support_rua@example.org\"\n", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "These DMARC records all specify rejection for email failing DMARC checks, but there are some differences:", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Each subdomain has a different email address for aggregate DMARC reports"),
  /*#__PURE__*/_createElementVNode("li", null, "The SPF domain alignment policy is strict for the domain itself but relaxed for the subdomains"),
  /*#__PURE__*/_createElementVNode("li", null, "The support subdomain gets weekly aggregate reports instead of daily")
], -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h2", null, "DNS tree walking in DMARC", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, "With three different DMARC records, how do mail servers determine which one to use?", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, "The answer is DNS tree walking using the domain portion of RFC5322.From field in each email message.", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "Every email contains an RFC5322.From field and an RFC5321.MailFrom field. They serve a different purpose:", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Mail clients display the email in the RFC5322.From field as the sender of an email."),
  /*#__PURE__*/_createElementVNode("li", null, "Mail servers send bounce emails to the email address in the RFC5321.MailFrom field when an email can't be delivered.")
], -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Formal specification for DNS tree walking in DMARC is ongoing in the Internet draft \""),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/draft-ietf-dmarc-dmarcbis/",
    rel: "noopener noreferrer"
  }, "draft-ietf-dmarc-dmarcbis"),
  /*#__PURE__*/_createTextVNode("\".")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This process involves walking up the DNS tree from the domain in the RFC5322.From field to search for a DMARC record. Using our example above, a receiving mail server would perform these DNS lookups if a message's RFC5322.From field is "),
  /*#__PURE__*/_createElementVNode("code", null, "sender@a.b.support.example.org"),
  /*#__PURE__*/_createTextVNode(":")
], -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("code", null, "_dmarc.a.b.support.example.org"),
    /*#__PURE__*/_createTextVNode(" --> No DMARC record found.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("code", null, "_dmarc.b.support.example.org"),
    /*#__PURE__*/_createTextVNode(" --> No DMARC record found.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("code", null, "_dmarc.support.example.org"),
    /*#__PURE__*/_createTextVNode(" --> DMARC record found!")
  ])
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", null, "DNS tree walking locates the DMARC record at the \"closest\" domain name at or above the RFC5322.From field. The algorithm also has a shortcut to prevent more than 5 DNS lookups. This shortcut prevents DMARC abuse by malicious parties.", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("h2", null, "Recipients will see the subdomain in emails", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "A drawback of using DMARC records for subdomains is that the address in the From field will appear to recipients of email as the subdomain and not the root domain for the business.", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Instead of coming from "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(", a tech support email would come from "),
  /*#__PURE__*/_createElementVNode("code", null, "support.example.org"),
  /*#__PURE__*/_createTextVNode(". It would be nice if all email from subdomains appeared to come from "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Perhaps relaxed SPF domain alignment mode would allow this? Unfortunately, no."),
  /*#__PURE__*/_createTextVNode(" Strict SPF domain alignment mode requires that the two From fields match exactly. Relaxed SPF domain alignment mode allows them to be different, so long as they are related. But the relationship is \"child only\". The RFC5321.MailFrom must be equal to or longer than the RFC5322.From domain.")
], -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This means that if RFC5322.From is "),
  /*#__PURE__*/_createElementVNode("code", null, "alice@support.example.org"),
  /*#__PURE__*/_createTextVNode(" then the RFC5321.MailFrom must be "),
  /*#__PURE__*/_createElementVNode("code", null, "support.example.org"),
  /*#__PURE__*/_createTextVNode(" in strict SPF domain alignment mode. Or some child domain of "),
  /*#__PURE__*/_createElementVNode("code", null, "support.example.org"),
  /*#__PURE__*/_createTextVNode(" in relaxed SPF domain alignment mode.")
], -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("h2", null, "Security holes and subdomains", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", null, "Another drawback is that subdomain administrators can inadvertently torpedo DMARC protection for the domain. Imagine that the domain has \"p=reject\" in its DMARC policy so that any email that fails DMARC checks is rejected. This is a good measure to combat spam.", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "Later, a subdomain DMARC record is created for promotional email. But the administrator for the subdomain doesn't understand DMARC very well. They decide to set \"p=none\" so that no promotional email is ever rejected by DMARC:", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("pre", null, "_dmarc.example.org.             3600  TXT  \"v=DMARC1; p=reject; rua=mailto:rua@example.org\"\n_dmarc.promotions.example.org.  3600  TXT  \"v=DMARC1; p=none; rua=mailto:rua@example.org\"\n", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This pattern of DMARC records could allow phishing and spam attacks to be spoofed from "),
  /*#__PURE__*/_createElementVNode("code", null, "promotions.example.org"),
  /*#__PURE__*/_createTextVNode(". Attackers could use this security hole to target customers of the business. This poor decision could harm the reputation of the company.")
], -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, "To prevent this sort of problem, administrators should review and approve all DMARC record changes anywhere in the domain to ensure that security standards are met.", -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("h2", null, "Finding the DMARC record for a domain", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, "DMARC records are stored as TXT records, so a standard TXT lookup will retrieve DMARC records. DMARC record names always begin with the label \"_dmarc\". The dig or nslookup command line tools can be used. To find the DMARC record for Google, use this command:", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("pre", null, "dig TXT _dmarc.google.com", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=TXT _dmarc.google.com", -1)
const _hoisted_33 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_34 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_35 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_36 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_37 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Does DMARC support inheritance? ", -1)
const _hoisted_38 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "No, DMARC does not support inheritance.")
], -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, "Mail Transfer Agents (MTAs) implementing DMARC follow specific steps to locate the DMARC record for each incoming email. This procedure always results in a single DMARC record. Or no DMARC record if the domain does not have one. ", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("inheritance is a common pattern in Computer Science. It's natural to assume in our example above that the DMARC policy for "),
  /*#__PURE__*/_createElementVNode("code", null, "promotions.example.org"),
  /*#__PURE__*/_createTextVNode(" might inherit some of its DMARC policy from the parent DMARC record at "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("p", null, "However, DMARC does not work this way. DMARC has no concept of inheritance. Each DMARC record fully specifies a policy. DMARC records at parent domains are irrelevant. Only the single DMARC record for the message's RFC5322.From domain applies.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import { learningBreadcrumb, emailBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

export const meta: BlogMeta = {
  tag: 'Email and the DNS',
  url: '/learning/using-different-dmarc-records-for-subdomains/',
  breadcrumbs: [learningBreadcrumb, emailBreadcrumb],
  title: 'Using different DMARC records for subdomains',
  description: "DMARC permits only one DMARC record per domain, but subdomains can be used when DMARC policies can't be merged.",
  overviewDescription: "DMARC permits only one DMARC record per domain, but subdomains can be used when DMARC policies can't be merged.",
  overviewImage: '/img/learning/using-different-dmarc-records-for-subdomains/multiple-dmarc-records-small.jpg',
  overviewImageAlt: 'Using different DMARC records for subdomains',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/using-different-dmarc-records-for-subdomains/using-different-dmarc-records-for-subdomains-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/using-different-dmarc-records-for-subdomains/using-different-dmarc-records-for-subdomains-social.jpg',
  written: new Date('2022-10-07T07:59:04.693Z'),
  updated: new Date('2023-03-28T11:59:38.386Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'EmailSecurityUsingDifferentDmarcRecordsForSubdomains',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/txt/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/using-different-dmarc-records-for-subdomains/multiple-dmarc-records.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _createElementVNode("strong", _hoisted_1, [
          _createTextVNode("Each domain name may have only one "),
          _createVNode(_component_router_link, { to: "/learning/dmarc-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("DMARC")
            ]),
            _: 1
          }),
          _createTextVNode(" record. When you connect multiple email services to a domain name, they will ask you to set different DMARC records. You can "),
          _createVNode(_component_router_link, { to: "/learning/how-to-merge-dmarc-records/" }, {
            default: _withCtx(() => [
              _createTextVNode("merge DMARC records")
            ]),
            _: 1
          }),
          _createTextVNode(", but that is not always practical.")
        ]),
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_5
          ]),
          _: 1
        }),
        _hoisted_6,
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _createVNode(Callout, {
          icon: "info",
          title: "What are RFC5322.From and RFC5321.MailFrom?",
          heading: "h3"
        }, {
          default: _withCtx(() => [
            _hoisted_11,
            _hoisted_12
          ]),
          _: 1
        }),
        _hoisted_13,
        _hoisted_14,
        _hoisted_15,
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_25
          ]),
          _: 1
        }),
        _hoisted_26,
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _hoisted_31,
        _hoisted_32,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the DMARC records for any domain name in our "),
          _createVNode(_component_router_link, { to: "/txt-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("TXT lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_33, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find TXT record",
            prefill: "_dmarc.",
            onSubmitted: redirectTo
          })
        ]),
        _createElementVNode("div", _hoisted_34, [
          _hoisted_35,
          _createElementVNode("div", _hoisted_36, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_37,
                    _createElementVNode("span", _hoisted_38, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_39,
                    _hoisted_40,
                    _hoisted_41,
                    _hoisted_42
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})